import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Icon, Spin, Tabs, Title, useToast } from "@app/ui";
import { api } from "../api/client.js";
import { useAuth } from "../auth/AuthContext.js";
import { EmailVerifyModal } from "../components/EmailVerifyModal.js";
import { useErrorText } from "../errors/index.js";
import { useT } from "../i18n.js";
import { AccountTab } from "../profile/AccountTab.js";
import { ActivityTab } from "../profile/ActivityTab.js";
import { DevicesTab } from "../profile/DevicesTab.js";
import { OrganizationsTab } from "../profile/OrganizationsTab.js";
import { ProfileHero } from "../profile/ProfileHero.js";
import { SecurityTab } from "../profile/SecurityTab.js";
import { TwoFactorTab } from "../profile/TwoFactorTab.js";
import { useSecurityChecks } from "../profile/SecurityChecklist.js";
import type { SecurityOverview, SessionInfo } from "../types.js";
import "../profile/profile.css";

/**
 * Account home. The band at the top answers "who am I, and is this account
 * safe?"; the rail below holds everything a user can change about themselves —
 * identity, security, devices, tenants, and the account's own history.
 *
 * Every panel is self-service (no permission gates): the API endpoints behind
 * them are all `/auth/*` and scoped to the caller.
 */

/** How many of the caller's own audit events the activity feed asks for. */
const ACTIVITY_LIMIT = 25;

export function ProfilePage() {
  const { user, refreshMe, logout } = useAuth();
  const t = useT();
  const toast = useToast();
  const errorText = useErrorText();
  const navigate = useNavigate();

  const [tab, setTab] = useState("account");
  const [sessions, setSessions] = useState<SessionInfo[]>([]);
  const [overview, setOverview] = useState<SecurityOverview | null>(null);
  const [verifyOpen, setVerifyOpen] = useState(false);
  const [downloading, setDownloading] = useState(false);

  const loadSessions = async () => {
    try {
      setSessions(await api<SessionInfo[]>("/auth/sessions"));
    } catch {
      /* the rest of the page still works without the device list */
    }
  };

  const loadOverview = async () => {
    try {
      setOverview(await api<SecurityOverview>(`/auth/security?limit=${ACTIVITY_LIMIT}`));
    } catch {
      /* checklist degrades to the two client-known checks */
    }
  };

  useEffect(() => {
    void loadSessions();
    void loadOverview();
  }, []);

  // Password change revokes every session server-side, so there is nothing left
  // to refresh — sign out locally and send the user back to the sign-in page.
  const afterPasswordChange = async () => {
    await logout();
    navigate("/login");
  };

  const downloadMyData = async () => {
    setDownloading(true);
    try {
      const data = await api<Record<string, unknown>>("/auth/export");
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "my-data.json";
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(errorText(e));
    } finally {
      setDownloading(false);
    }
  };

  const checks = useSecurityChecks(user, overview, {
    verifyEmail: () => setVerifyOpen(true),
    enableMfa: () => setTab("2fa"),
    changePassword: () => setTab("security"),
  });

  if (!user) return <Spin />;

  const done = checks.filter((c) => c.done).length;

  return (
    <div className="pf-page">
      <Title level={3}>{t("nav.profile")}</Title>

      <ProfileHero
        user={user}
        score={{ done, total: checks.length }}
        onVerifyEmail={() => setVerifyOpen(true)}
        onDownloadData={() => void downloadMyData()}
        downloading={downloading}
      />

      <Tabs
        orientation="vertical"
        activeKey={tab}
        onChange={setTab}
        items={[
          {
            key: "account",
            label: t("profile.tabProfile"),
            icon: <Icon name="user" size={16} />,
            children: (
              <AccountTab
                user={user}
                onDownloadData={() => void downloadMyData()}
                downloading={downloading}
              />
            ),
          },
          {
            key: "security",
            label: t("profile.tabSecurity"),
            icon: <Icon name="lock" size={16} />,
            children: (
              <SecurityTab checks={checks} overview={overview} onPasswordChanged={afterPasswordChange} />
            ),
          },
          {
            key: "2fa",
            label: t("profile.tab2fa"),
            icon: <Icon name="shield" size={16} />,
            children: (
              <TwoFactorTab
                enabled={user.mfaEnabled}
                onChange={async () => {
                  await refreshMe();
                  await loadSessions();
                }}
              />
            ),
          },
          {
            key: "devices",
            label: t("profile.tabDevices"),
            icon: <Icon name="monitor" size={16} />,
            children: <DevicesTab sessions={sessions} reload={loadSessions} />,
          },
          {
            key: "orgs",
            label: t("profile.tabOrgs"),
            icon: <Icon name="building" size={16} />,
            children: <OrganizationsTab />,
          },
          {
            key: "activity",
            label: t("profile.tabActivity"),
            icon: <Icon name="clock" size={16} />,
            children: <ActivityTab events={overview?.recentEvents ?? []} />,
          },
        ]}
      />

      <EmailVerifyModal
        open={verifyOpen}
        onClose={() => {
          setVerifyOpen(false);
          void loadOverview();
        }}
      />
    </div>
  );
}
